import { GeoJsonLayer } from '@deck.gl/layers';
import { Route } from '@helpers/types';
import { hexToRgb } from '@utils/colors';
import { Feature, MultiLineString, Point } from 'geojson';
import { chain } from 'lodash';
import { useMemo } from 'react';

interface UseDeckRoutesProps {
  routes: Array<Route> | undefined;
  polylines?: Array<Array<GeoJSON.FeatureCollection<MultiLineString | Point>>>;
  selectedRoutes?: string[];
  hoveredRoute?: string | null;
}

export const useDeckRoutes = ({
  routes,
  polylines,
  selectedRoutes = [],
  hoveredRoute = null,
}: UseDeckRoutesProps) => {
  const layers = useMemo(() => {
    if (!polylines || polylines.length === 0 || !routes) return [];

    // Flatten polylines and associate with route colors
    const allPolylines: Feature<MultiLineString>[] = chain(polylines)
      .map((routePolylines, routeIndex) => {
        const route = routes[routeIndex];
        if (!route || !routePolylines || routePolylines.length === 0) return [];

        return routePolylines.map((featureCollection) => {
          return featureCollection.features
            .filter((feature) => feature.geometry.type === 'MultiLineString')
            .map((feature) => ({
              type: 'Feature' as const,
              geometry: feature.geometry as MultiLineString,
              properties: {
                route: route.name,
                color: route.color,
                routeId: route.id,
              },
            }));
        }).flat();
      })
      .flatten()
      .filter((f) => {
        return f &&
               f.geometry &&
               f.geometry.coordinates &&
               Array.isArray(f.geometry.coordinates) &&
               f.geometry.coordinates.length > 0;
      })
      .value();

    if (allPolylines.length === 0) return [];

    // Base layer: Grey lines
    const baseLayer = new GeoJsonLayer({
      id: 'routes-base',
      data: allPolylines,
      pickable: false,
      stroked: true,
      filled: false,
      lineWidthMinPixels: 2,
      getLineColor: [174, 174, 174, 180], // #AEAEAE with some transparency
      getLineWidth: 2,
    });

    // Highlight layer: Colored lines for selected or hovered routes
    const highlightPolylines = allPolylines.filter(
      (p) =>
        selectedRoutes.includes(p.properties!.route) ||
        p.properties!.route === hoveredRoute,
    );

    const highlightLayer = new GeoJsonLayer({
      id: 'routes-highlight',
      data: highlightPolylines,
      pickable: false,
      stroked: true,
      filled: false,
      lineWidthMinPixels: 3,
      getLineColor: (d: any) => [...hexToRgb(d.properties.color), 255],
      getLineWidth: 3,
    });

    return highlightPolylines.length > 0
      ? [baseLayer, highlightLayer]
      : [baseLayer];
  }, [polylines, routes, selectedRoutes, hoveredRoute]);

  return { layers };
};
